import { Fragment, FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import { WhitelabelSettingReadModel } from "../../../../../@api";

import { translationKeys } from "../../../../models/app-translation-keys";
import { applyWhitelabelSettings } from "../../../../models/functions/apply-whitelabel-settings.function";
import { useWhitelabelService } from "../../../../services/ServiceHooks/whitelabelHook";
import { useStore } from "../../../../stores/StoreContext";
import { InfoBox, TextMarkdown } from "../../../Shared/Components";
import { confirmAction } from "../../../Shared/Functions";
import { ColorSettings } from "./Contents/Color/ColorSettings";
import { FontSettings } from "./Contents/Font/FontSettings";
import { LayoutSettings } from "./Contents/Layout/LayoutSettings";
import { WhiteLabelSegment } from "./WhilteLabelSegment/WhiteLabelSegment";

import "./WhiteLabelTab.scss";

export interface IWhiteLabelSegment {
  headerKey: string;
  descriptionMarkdownKey: string;
  contentComponent: ReactNode;
}

const WhiteLabelTabRaw: FunctionComponent = () => {
  const { t } = useTranslation();
  const { whitelabelStore } = useStore();
  const [settings, setSettings] = useState<WhitelabelSettingReadModel[]>([]);
  const [isAnySettingDirty, setIsAnySettingDirty] = useState(false);
  const whitelabelService = useWhitelabelService();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      await whitelabelService.getGeneralSettings();
      setIsLoading(false);
    };

    if (!settings?.length) {
      loadSettings();
    }
  }, []);

  useEffect(() => {
    setSettings(whitelabelStore.whitelabelSettings || []);
  }, [whitelabelStore.whitelabelSettings]);

  useEffect(() => {
    const isAnySettingDirty = settings.some(setting => setting.customValue !== setting.defaultValue);
    setIsAnySettingDirty(isAnySettingDirty);
  }, [settings]);

  const resetAllToDefault = async () => {
    const confirmed = await confirmAction(t, {
      acceptLabel: t(whiteLabelTranslationKeys.actions.resetAllToDefault.dialog.acceptLabel),
      message: t(whiteLabelTranslationKeys.actions.resetAllToDefault.dialog.message),
    });

    if (!confirmed) {
      return;
    }

    await whitelabelService.resetAllSettingsToDefault();
    const defaultSettings = await whitelabelService.getGeneralSettings();
    applyWhitelabelSettings(defaultSettings);
    setSettings(defaultSettings);
    setIsAnySettingDirty(false);
  };

  const resetSetting = async (key: string) => {
    whitelabelService.resetSettingsToDefault(key);
  };

  const whiteLabelTranslationKeys = translationKeys.adminArea.whiteLabel;

  const whiteLabelSegments: IWhiteLabelSegment[] = [
    {
      headerKey: whiteLabelTranslationKeys.areas.font.header,
      descriptionMarkdownKey: whiteLabelTranslationKeys.areas.font.descriptionMarkdown,
      contentComponent: <FontSettings settings={settings} resetSetting={resetSetting} />,
    },
    {
      headerKey: whiteLabelTranslationKeys.areas.color.header,
      descriptionMarkdownKey: whiteLabelTranslationKeys.areas.color.descriptionMarkdown,
      contentComponent: <ColorSettings settings={settings} resetSetting={resetSetting} />,
    },
    {
      headerKey: whiteLabelTranslationKeys.areas.layout.header,
      descriptionMarkdownKey: whiteLabelTranslationKeys.areas.layout.descriptionMarkdown,
      contentComponent: <LayoutSettings settings={settings} resetSetting={resetSetting} />,
    },
    /*{
      headerKey: whiteLabelTranslationKeys.areas.translations.header,
      descriptionMarkdownKey: whiteLabelTranslationKeys.areas.translations.descriptionMarkdown,
      contentComponent: <TranslationSettings settings={settings} resetSetting={resetSetting} />,
    },*/
  ];

  return (
    <div className="white-label-tab">
      <div className="white-label-tab-header">
        <h1>{t(whiteLabelTranslationKeys.tabHeader)}</h1>
      </div>

      <div className="white-label-tab-description">
        <TextMarkdown>{t(whiteLabelTranslationKeys.tabDescriptionMarkdown)}</TextMarkdown>
      </div>

      <InfoBox icon={faCircleInfo} infoTranslationKey={whiteLabelTranslationKeys.tabAutoSaveInformation} />

      {!!isLoading && (
        <div className="loading-spinner">
          <ProgressSpinner className="medium-inline-spinner" />
        </div>
      )}

      {!isLoading && (
        <Fragment>
          <div className="white-label-segments">
            {whiteLabelSegments.map(segment => (
              <WhiteLabelSegment key={segment.headerKey} segment={segment} />
            ))}
          </div>

          {!!isAnySettingDirty && (
            <Button
              className="reset-settings-button"
              style={{ marginTop: "1rem" }}
              onClick={resetAllToDefault}
              severity="danger"
            >
              {t(whiteLabelTranslationKeys.actions.resetAllToDefault.buttonLabel)}
            </Button>
          )}
        </Fragment>
      )}
    </div>
  );
};

export const WhiteLabelTab = observer(WhiteLabelTabRaw);
